import {
  BaseEventService,
  type ServiceActivationContext,
} from '@realitycollective/service-framework';
import { createLogger } from '../../util/logger';
import { AssistConsentMachine, type AssistConsentState } from '../../core/assistConsentMachine';
import { assistantAudioTile, micShouldPublish, micTile, type AudioTile } from '../../core/wearerAudioPolicy';
import { slugFromIdentity } from '../../core/assistantRoster';
import type { ILiveKitRoomService } from './ILiveKitRoomService';
import type { IAssistantRosterService } from './IAssistantRosterService';
import type { AssistSessionEventMap, IAssistSessionService } from './IAssistSessionService';

const log = createLogger('Assist');

export interface AssistSessionConfig {
  /**
   * Consent default when the remote config's assist block has no opinion (D6:
   * config-controlled auto-allow - demo/kiosk mode). The served
   * `assist.autoAllow` overrides this at runtime; the on-air indicator shows
   * whenever assistance is active regardless. Default true.
   */
  readonly autoAllowDefault?: boolean;
}

/**
 * The consent machine's consequences. Port of `AssistSessionService.cs`.
 *
 * - live: mic published/unmuted, panels per assistant state, indicator lit.
 * - paused: mic hard-muted the moment the action lands, every assistant track
 *   unsubscribed via the roster, indicator amber, presence broadcasts `paused`.
 * - off/awaiting: mic never published; the menu offers Allow when the config
 *   withheld auto-allow (D6).
 *
 * On top of consent sits the wearer's OWN microphone mute - a separate choice
 * with a separate control, because "stop the room hearing me for a moment"
 * and "withdraw consent to be assisted" are different intentions and only one
 * of them should tear down the session. The two compose in
 * `wearerAudioPolicy` and only ever subtract.
 *
 * Web note: the Unity port pushes tile state into a panel service; here the
 * tiles are pulled (`micTile`, `assistantAudioTile`) and `tilesChanged` says
 * when to re-render - the UX layer owns the presentation.
 */
export class AssistSessionService
  extends BaseEventService<AssistSessionEventMap, AssistSessionConfig>
  implements IAssistSessionService
{
  private readonly room: ILiveKitRoomService;
  private readonly roster: IAssistantRosterService;
  private readonly machine = new AssistConsentMachine();
  private readonly legQuality = new Map<string, string>();
  private readonly unsubs: Array<() => void> = [];

  private remoteAutoAllow: boolean | null = null;

  /** The wearer's own mic mute - their choice, not consent's. */
  private selfMuted = false;

  /** Last EFFECTIVE mic state pushed to the room, so the change event fires once per real transition. */
  private micLive = false;

  constructor(
    context: ServiceActivationContext<AssistSessionConfig>,
    room: ILiveKitRoomService,
    roster: IAssistantRosterService,
  ) {
    super(context);
    this.room = room;
    this.roster = roster;
  }

  get state(): AssistConsentState {
    return this.machine.state;
  }

  get awaitingAllow(): boolean {
    return this.machine.awaitingAllow;
  }

  get microphoneMuted(): boolean {
    return this.selfMuted;
  }

  get microphoneLive(): boolean {
    return this.micLive;
  }

  get micTile(): AudioTile {
    return micTile(this.machine.state, this.selfMuted);
  }

  get assistantAudioTile(): AudioTile {
    return assistantAudioTile(this.machine.state, this.roster.assistantsMuted, this.roster.count);
  }

  override start(): void {
    this.unsubs.push(
      this.machine.onStateChanged((state) => this.onConsentChanged(state)),
      this.room.on('stateChange', (state) => {
        switch (state) {
          case 'connected':
            this.machine.sessionStarted(this.effectiveAutoAllow);
            break;
          case 'failed':
          case 'closed':
            this.legQuality.clear();
            this.machine.sessionEnded();
            break;
        }
      }),
      // RA-C10/D6: the served config's assist block decides the entry, and a
      // late arrival promotes a waiting session.
      this.room.on('remoteConfig', (config) => this.applyRemoteAutoAllow(config.assistAutoAllow)),
      this.room.on('participantQuality', ({ identity, quality }) => this.onParticipantQuality(identity, quality)),
      this.roster.on('assistantJoined', () => this.emit('tilesChanged', undefined)),
      this.roster.on('assistantLeft', () => this.emit('tilesChanged', undefined)),
      this.roster.on('allMutedChanged', () => this.emit('tilesChanged', undefined)),
    );

    // The room may already be connected when this service starts (service
    // ordering); a state-change that already happened never replays.
    if (this.room.isRoomConnected) this.machine.sessionStarted(this.effectiveAutoAllow);
  }

  override destroy(): void {
    this.unsubs.forEach((unsub) => unsub());
    this.unsubs.length = 0;
    super.destroy();
  }

  masterAction(): void {
    this.machine.masterAction();
  }

  setMicrophoneMuted(muted: boolean): void {
    if (this.selfMuted === muted) return;
    this.selfMuted = muted;
    log.info(`Wearer microphone ${muted ? 'muted' : 'unmuted'} by hand menu`);
    this.applyMicrophone();
    this.emit('tilesChanged', undefined);
  }

  toggleMicrophoneMuted(): void {
    this.setMicrophoneMuted(!this.selfMuted);
  }

  applyRemoteAutoAllow(autoAllow: boolean | null): void {
    this.remoteAutoAllow = autoAllow;
    if (autoAllow !== null) {
      log.info(`Remote config assist.autoAllow = ${autoAllow}`);
      this.machine.autoAllowUpdated(autoAllow);
    }
  }

  private get effectiveAutoAllow(): boolean {
    return this.remoteAutoAllow ?? this.serviceConfig.autoAllowDefault ?? true;
  }

  /**
   * Push the EFFECTIVE microphone state at the room and announce it. One path
   * for both inputs (consent and the wearer's own mute) so they can never
   * disagree about what the mic is doing.
   */
  private applyMicrophone(): void {
    const live = micShouldPublish(this.machine.state, this.selfMuted);

    // Unconditional, not only on change: the room service is the owner of
    // "is a track published", and a re-assert is how a mic that failed to
    // publish gets another go.
    this.room.setMicrophoneEnabled(live);

    if (live === this.micLive) return;
    this.micLive = live;
    this.emit('microphoneLiveChanged', live);
  }

  /** Every consequence of a consent transition, in one place. */
  private onConsentChanged(state: AssistConsentState): void {
    // Mic first: a pause must kill the mic before anything cosmetic.
    this.applyMicrophone();

    // Subscriptions + presence echo (live restores wearer choices, paused
    // unsubscribes everything, off broadcasts nothing).
    this.roster.setAssistState(this.machine.assistToken);

    log.info(`Consent state: ${state}`);
    this.emit('stateChange', state);
    this.emit('tilesChanged', undefined);
  }

  private onParticipantQuality(identity: string, quality: string): void {
    if (!identity) return;
    this.legQuality.set(identity, quality);

    // One compact line for every leg the SFU reports (D10's out-of-band
    // failure signal): "wearer good · assistant:jane poor".
    const line = [...this.legQuality.entries()]
      .map(([leg, value]) => `${slugFromIdentity(leg)} ${value}`)
      .join(' · ');
    const severity = quality === 'poor' || quality === 'lost' ? 'warning' : 'ok';
    this.emit('qualityChanged', { line, severity });
  }
}
